import { Link } from "react-router-dom";
import clsx from "clsx";

const buttonSizes = {
  sm: "py-1.5 px-3 text-xs",
  md: "py-2 px-4 text-sm",
  lg: "py-2.5 px-6 text-sm",
  xl: "py-3 px-8 text-md",
};

const baseStyles = {
  solid:
    "group inline-flex items-center justify-center rounded-full font-semibold focus-visible:outline-2 focus-visible:outline-offset-2",
  outline: "group inline-flex ring-1 items-center justify-center rounded-full",
  text: "group inline-flex items-center justify-center font-semibold focus-visible:outline-2 focus-visible:outline-offset-2",
};

const variantStyles = {
  solid: {
    primary:
      "cursor-pointer bg-primary text-white hover:bg-primary-dark active:bg-primary-dark focus-visible:outline-primary",
    slate:
      "cursor-pointer bg-slate-900 text-white hover:bg-slate-700 hover:text-slate-100 active:bg-slate-800 active:text-slate-300 focus-visible:outline-slate-900",
    blue: "cursor-pointer bg-blue-100 text-white hover:text-slate-100 hover:bg-blue-500 active:bg-blue-800 active:text-blue-100 focus-visible:outline-primary",
    primaryLight:
      "cursor-pointer bg-primary-light text-white hover:text-dark hover:bg-primary-dark active:bg-primary-dark active:text-primary-100 focus-visible:outline-primary",
    secondary:
      "cursor-pointer bg-secondary-light text-white hover:bg-secondary-light active:bg-secondary-dark focus-visible:outline-secondary",
    white:
      "cursor-pointer bg-white text-slate-900 hover:bg-blue-50 active:bg-blue-200 active:text-slate-600 focus-visible:outline-white",
    error:
      "cursor-pointer bg-red-600 text-white hover:bg-red-500 active:bg-red-800 active:text-red-100 focus-visible:outline-red-600",
  },
  outline: {
    slate:
      "cursor-pointer ring-slate-200 text-slate-700 hover:text-slate-900 hover:ring-slate-300 active:bg-slate-100 active:text-slate-600 focus-visible:outline-primary focus-visible:ring-slate-300",
    white:
      "cursor-pointer ring-slate-700 text-white hover:ring-slate-500 active:ring-slate-700 active:text-slate-400 focus-visible:outline-white",
    error:
      "cursor-pointer ring-red-200 text-red-700 hover:text-red-900 hover:ring-red-300 active:bg-red-100 active:text-red-600 focus-visible:outline-primary focus-visible:ring-red-300",
  },
  text: {
    primaryLight:
      "cursor-pointer text-primary-light hover:text-primary-dark active:text-primary-dark focus-visible:outline-primary",
    primary:
      "cursor-pointer text-primary hover:text-primary-dark active:text-primary-dark focus-visible:outline-primary",
    slate:
      "cursor-pointer text-slate-700 hover:text-slate-900 active:text-slate-600 focus-visible:outline-slate-700",
    blue: "cursor-pointer text-blue-500 hover:text-blue-600 active:text-blue-700 focus-visible:outline-blue-500",
    white:
      "cursor-pointer text-white hover:text-slate-200 active:text-slate-300 focus-visible:outline-white",
    error:
      "cursor-pointer text-red-600 hover:text-red-700 active:text-red-500 focus-visible:outline-red-600",
  },
};

export function Button({
  className,
  size = "md",
  loading = false,
  children,
  ...props
}) {
  props.variant ??= "solid";
  props.color ??= "slate";

  const variantClass = variantStyles[props.variant]?.[props.color];

  className = clsx(
    baseStyles[props.variant],
    buttonSizes[size],
    variantClass,
    className
  );

  const content = (
    <>
      {loading && (
        <svg
          className="animate-spin h-4 w-4 mr-2"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="1.5"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      )}
      {children}
    </>
  );

  return typeof props.to === "undefined" ? (
    <button
      className={className}
      disabled={loading || props.disabled}
      {...props}
    >
      {content}
    </button>
  ) : (
    <Link className={className} {...props}>
      {content}
    </Link>
  );
}
